import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeMode, Standard, DownCategory, DataSource } from '../types';

// ═══════════════════════════════════════════════════
// UI 状态 Store
// ═══════════════════════════════════════════════════

interface UIState {
  theme: ThemeMode;
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;

  // Actions
  setTheme: (theme: ThemeMode) => void;
  toggleSidebar: () => void;
  setSidebarMobileOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'light',
      sidebarCollapsed: false,
      sidebarMobileOpen: false,

      setTheme: (theme) => set({ theme }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarMobileOpen: (open) => set({ sidebarMobileOpen: open }),
    }),
    {
      name: 'down-data-ui',
      partialize: (state) => ({ theme: state.theme, sidebarCollapsed: state.sidebarCollapsed }),
    }
  )
);

// ═══════════════════════════════════════════════════
// 筛选状态 Store
// ═══════════════════════════════════════════════════

interface FilterStoreState {
  standards: Standard[];
  categories: DownCategory[];
  sources: DataSource[];
  date: string;

  // Actions
  toggleStandard: (standard: Standard) => void;
  toggleCategory: (category: DownCategory) => void;
  toggleSource: (source: DataSource) => void;
  setDate: (date: string) => void;
  resetFilters: () => void;
}

// 默认不筛选，显示所有数据
const defaultStandards: Standard[] = [];
const defaultCategories: DownCategory[] = [];
const defaultSources: DataSource[] = [];

export const useFilterStore = create<FilterStoreState>()(
  persist(
    (set) => ({
      standards: defaultStandards,
      categories: defaultCategories,
      sources: defaultSources,
      date: new Date().toISOString().split('T')[0],

      toggleStandard: (standard) => set((state) => ({
        standards: state.standards.includes(standard)
          ? state.standards.filter((s) => s !== standard)
          : [...state.standards, standard],
      })),

      toggleCategory: (category) => set((state) => ({
        categories: state.categories.includes(category)
          ? state.categories.filter((c) => c !== category)
          : [...state.categories, category],
      })),

      toggleSource: (source) => set((state) => ({
        sources: state.sources.includes(source)
          ? state.sources.filter((s) => s !== source)
          : [...state.sources, source],
      })),

      setDate: (date) => set({ date }),

      resetFilters: () => set({
        standards: defaultStandards,
        categories: defaultCategories,
        sources: defaultSources,
        date: new Date().toISOString().split('T')[0],
      }),
    }),
    {
      name: 'down-data-filters',
    }
  )
);

// ═══════════════════════════════════════════════════
// 价格数据 Store
// ═══════════════════════════════════════════════════

import type { PriceEntry, PriceTrendPoint } from '../types';

interface PriceState {
  entries: PriceEntry[];
  trendData: Record<string, PriceTrendPoint[]>;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;

  // Actions
  setEntries: (entries: PriceEntry[]) => void;
  setTrendData: (key: string, data: PriceTrendPoint[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  refresh: () => Promise<void>;
}

export const usePriceStore = create<PriceState>((set) => ({
  entries: [],
  trendData: {},
  loading: false,
  error: null,
  lastUpdated: null,

  setEntries: (entries) => set({
    entries,
    lastUpdated: new Date().toISOString()
  }),

  setTrendData: (key, data) => set((state) => ({
    trendData: { ...state.trendData, [key]: data },
  })),

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      // TODO: 调用 API 获取数据
      // const data = await priceApi.getLatest();
      // set({ entries: data, loading: false });
      set({ loading: false });
    } catch (err) {
      set({ error: String(err), loading: false });
    }
  },
}));

// ═══════════════════════════════════════════════════
// 新闻数据 Store
// ═══════════════════════════════════════════════════

import type { NewsItem } from '../types';

interface NewsState {
  items: NewsItem[];
  loading: boolean;
  error: string | null;

  // Actions
  setItems: (items: NewsItem[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useNewsStore = create<NewsState>((set) => ({
  items: [],
  loading: false,
  error: null,

  setItems: (items) => set({ items }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
}));
